import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Check, Lock, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

// Suppress cross-origin iframe errors from analytics scripts
if (typeof window !== 'undefined') {
  const originalError = console.error;
  console.error = (...args) => {
    if (args[0]?.includes?.('cross-origin') || args[0]?.includes?.('removeEventListener')) {
      return;
    }
    originalError.apply(console, args);
  };
}

export default function Checkout() {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const createOrder = async () => {
    try {
      const response = await fetch(`${API_URL}/api/checkout/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: "197.00",
          product: "calm_scale_framework"
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Create order failed:', errorText);
        throw new Error('Failed to create order');
      }
      
      const data = await response.json();
      
      if (!data.order_id) {
        throw new Error('No order_id returned from server');
      }
      
      return data.order_id;
    } catch (error) {
      console.error('Create order error:', error);
      throw error;
    }
  };

  const onApprove = async (data) => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/checkout/capture-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          order_id: data.orderID
        })
      });
      
      const responseData = await response.json();
      
      if (response.ok && responseData.status === 'COMPLETED') {
        navigate('/checkout/success', { replace: true });
      } else {
        console.error('Capture response:', responseData);
        throw new Error('Capture failed');
      }
    } catch (error) {
      console.error('Capture error:', error);
      navigate('/checkout/cancel', { replace: true });
    } finally {
      setIsProcessing(false);
    }
  };

  const onCancel = () => {
    navigate('/checkout/cancel');
  };

  const onError = (err) => {
    console.error('PayPal error:', err);
    navigate('/checkout/cancel');
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-sm border-b border-[#E5E0D6]">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-xl text-[#2C2C2C]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Calm Scale™
          </Link>
          <div className="flex items-center gap-2 text-[#2C2C2C]/50 text-sm">
            <Lock className="w-4 h-4" />
            Secure Checkout
          </div>
        </div>
      </nav>

      <main className="pt-28 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Headline */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Enter the Calm Scale™ 14-Day Framework
            </h1>
          </div>

          {/* Subtext */}
          <div className="mb-12 text-center">
            <p className="text-[#2C2C2C]/70 font-light mb-4">
              Calm Scale™ is a structured build method designed to help you create a profitable digital asset in 14 focused days.
            </p>
            <p className="text-[#2C2C2C]/80 mb-4">
              This is a one-time investment in a repeatable system.
            </p>
            <div className="space-y-1">
              <p className="text-[#2C2C2C]">Not for information.</p>
              <p className="text-[#6B8E7C] font-medium">For execution.</p>
            </div>
          </div>

          {/* What You Receive */}
          <div className="card-calm mb-8">
            <p className="text-xs text-[#D4A373] font-medium tracking-widest uppercase mb-4">
              What You Receive
            </p>
            <p className="text-lg text-[#2C2C2C] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Your $197 investment includes:
            </p>
            
            <ul className="space-y-3 mb-8">
              {[
                "The full 14-Day Calm Scale™ framework",
                "Clarity positioning system",
                "Asset Builder structure",
                "Authority positioning model",
                "Calm Launch structure",
                "Guided outputs in every phase",
                "Lifetime access"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#6B8E7C] flex-shrink-0 mt-0.5" />
                  <span className="text-[#2C2C2C]/70">{item}</span>
                </li>
              ))}
            </ul>

            <div className="pt-6 border-t border-[#E5E0D6] space-y-1">
              <p className="text-[#2C2C2C]">No subscriptions.</p>
              <p className="text-[#2C2C2C]">No hidden tiers.</p>
              <p className="text-[#2C2C2C]">No ongoing fees.</p>
              <p className="text-[#6B8E7C] font-medium mt-4">One structured system.</p>
            </div>
          </div>

          {/* Price Block */}
          <div className="bg-white border border-[#E5E0D6] rounded-xl p-8 mb-8 text-center">
            <p className="text-xs text-[#2C2C2C]/50 uppercase tracking-wide mb-2">
              Investment
            </p>
            <p className="text-5xl text-[#2C2C2C] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              $197
            </p>
            <p className="text-sm text-[#2C2C2C]/50 mb-4">USD • One-Time Payment</p>
            
            <div className="text-sm text-[#2C2C2C]/60 space-y-1">
              <p>Secure checkout via PayPal.</p>
              <p>Instant access upon confirmation.</p>
            </div>
          </div>

          {/* Agreement Checkbox */}
          <div className="flex items-start gap-3 mb-6 p-4 bg-[#F2F0EB]/50 rounded-xl">
            <Checkbox 
              id="agreement" 
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-0.5"
              data-testid="agreement-checkbox"
            />
            <label htmlFor="agreement" className="text-sm text-[#2C2C2C]/70 cursor-pointer">
              I understand this is a digital product and receive instant access upon purchase.
            </label>
          </div>

          {/* PayPal Button or Fallback */}
          <div className={`transition-opacity ${agreed ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
            {PAYPAL_CLIENT_ID ? (
              <PayPalScriptProvider options={{ 
                clientId: PAYPAL_CLIENT_ID,
                currency: "USD"
              }}>
                <PayPalButtons
                  style={{
                    layout: "vertical",
                    color: "black",
                    shape: "rect",
                    label: "pay",
                    height: 50
                  }}
                  disabled={!agreed || isProcessing}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onCancel={onCancel}
                  onError={onError}
                />
              </PayPalScriptProvider>
            ) : (
              <Button
                onClick={async () => {
                  if (!agreed) return;
                  setIsProcessing(true);
                  try {
                    const response = await fetch(`${API_URL}/api/checkout/create-order`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ amount: "197.00", product: "calm_scale_framework" })
                    });
                    
                    const data = await response.json();
                    
                    if (response.ok && data.order_id) {
                      // For demo mode without PayPal, simulate successful order
                      if (data.demo) {
                        // Capture the demo order
                        const captureResponse = await fetch(`${API_URL}/api/checkout/capture-order`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ order_id: data.order_id })
                        });
                        const captureData = await captureResponse.json();
                        
                        if (captureResponse.ok && captureData.status === 'COMPLETED') {
                          navigate('/checkout/success', { replace: true });
                        } else {
                          throw new Error('Capture failed');
                        }
                      } else {
                        navigate('/checkout/success', { replace: true });
                      }
                    } else {
                      throw new Error('Order creation failed');
                    }
                  } catch (error) {
                    console.error('Order error:', error);
                    navigate('/checkout/cancel', { replace: true });
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                disabled={!agreed || isProcessing}
                className="w-full btn-primary py-4 text-base"
                data-testid="checkout-btn"
              >
                {isProcessing ? 'Processing...' : 'Complete Purchase'}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Microcopy */}
          <p className="text-xs text-[#2C2C2C]/40 text-center mt-4">
            You will be redirected to PayPal to complete your secure transaction.
          </p>

          {/* Back Link */}
          <div className="mt-12 text-center">
            <Link to="/" className="text-sm text-[#2C2C2C]/50 hover:text-[#6B8E7C] transition-colors">
              ← Return to Calm Scale™
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
